import {
  FC,
  HTMLAttributes,
  ReactNode,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader
} from '@/components/modal/';
import { ClaimItem } from './index';
import Button from '@/components/button';
import {
  apiClaimSbt,
  apiGetSbtDetail,
  apiGetSbtList,
  apiPreviewSbt
} from '@/apis/sbt';
import toast from '@/components/toast';
import BgImg from '@/assets/bg_cover.svg';
import { useNavigate } from 'react-router-dom';
import Confetti from 'react-confetti';
import IconLogoLoading from '@/assets/logo_loading.svg';
import idIcon from '@/assets/id_icon.svg';
import dcLogo from '@/assets/donation/dclogo.svg';
import gitcoinLogo from '@/assets/donation/gitcoinlogo.jpg';
import Typography from '@/components/typography';
import {
  apiGetGitcoinVery,
  apiOrgGotcoinOrg,
  apiSendGitcoinSbt
} from '@/apis/donation';
import { apiGetUserInfo } from '@/apis/user';
import { apiGetOpt } from '@/apis/home';
import { formatDate } from '@/utils/helpers';
import { useAppSetting } from '@/store/appSetting';

interface Props extends HTMLAttributes<HTMLElement> {
  children?: ReactNode;
  item?: ClaimItem;
  title?: string;
  confirmText?: string;
  cancelText?: string;
  onCancel?: () => void;
  onConfirm?: () => void;
  onClose?: () => void;
  onGetSbt?: () => void;
}

const ClaimModal: FC<Props> = (props: Props) => {
  const { auth } = useAppSetting();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 0 no donation, 1,detail 2, minting 3. mint success, 4 minted
  const [loading, setLoading] = useState(true);
  const [pageload, setPageload] = useState(true);
  const [item, setItem] = useState<any>();
  const [detail, setDetail] = useState<any>();
  const [preview, setPreview] = useState('');
  const renderRef = useRef(true);
  const [showConfetti, setShowConfetti] = useState(true);
  const [orgId, setOrgId] = useState(import.meta.env.VITE_ORGID_GITCOIN);
  let timers;

  const handlePreview = async (data: {
    orgId: string;
    sbtAvatar: string;
    sbtName: string;
    type: string;
    isUserSbt: boolean;
  }) => {
    const userinfo = await apiGetUserInfo(
      auth.address || localStorage.getItem('address') || ''
    );
    const res = await apiPreviewSbt({
      ...data,
      userAvatar: userinfo.avatar,
      username: userinfo.address
    });
    if (res) {
      setPreview(res.preview);
    }
  };

  const handleClaimSbt = async (action: 'accept' | 'reject') => {
    if (action === 'accept') setStep(2);
    const sbtId = item?.id || 0;
    const res = await apiClaimSbt({
      action,
      message: '', // TODO:
      orgId: orgId || '',
      sig: '', // TODO:
      tokenId: item?.tokenId || 0,
      userSbtId: +sbtId
    });
    if (res) {
      // toast.success('Mint success!');
      props.onGetSbt ? props.onGetSbt() : null;
      // handleSearchOpt(res.operationId, action);
      setStep(action === 'accept' ? 3 : 5);
      // action === 'accept' ? handleSearchOpt(res.operationId, action) : null;
    } else {
      // toast.error('Update error!');
      setStep(0);
    }
  };

  const handleSearchOpt = async (id, action) => {
    const data = await apiGetOpt(id);
    if (data.state === 100) {
      setStep(action === 'accept' ? 3 : 5);
      clearTimeout(timers);
      setShowConfetti(true);
      setTimeout(() => {
        setShowConfetti(false);
      }, 4500);
    } else {
      timers = setTimeout(() => {
        handleSearchOpt(id, action);
      }, 1500);
    }
  };

  const handleShareTwitter = async () => {
    const text = `Just free-minted a Noun-transferable NFT for my @Gitcoin donation through @Vitae3me . Claim your Gitcoin Donation NFT here: `;
    const url = `${
      import.meta.env.VITE_TWITTER_SHARE_URL
    }/api/v1/share/sbt-pic/${item?.org.id}/${item?.receiverAddress}/${
      item?.tokenId
    }?redirectPath=${encodeURIComponent('/donation/gitcoin')}`;
    const twitterUrl = `https://twitter.com/share?text=${encodeURIComponent(
      text
    )}&url=${url}`;
    window.open(twitterUrl);
  };

  const handleSendSbt = async () => {
    setPageload(true);
    const sbtRes = await apiSendGitcoinSbt();
    if (sbtRes) {
      handleGetVery();
    }
    setPageload(false);
  };

  const handleSearchSbt = async () => {
    setPageload(true);
    const orgData: any = await apiOrgGotcoinOrg();
    if (orgData) {
      setOrgId(orgData.id);
      const res = await apiGetSbtList({ state: '0', type: 'donation' });
      if (res.items && res.items.length > 0) {
        setItem(res.items[0]);
        handlePreview({
          orgId,
          sbtAvatar: res.items[0]?.avatar || '',
          sbtName: res.items[0]?.name || '',
          type: 'donation',
          isUserSbt: true
        });
        const data: any = await apiGetSbtDetail({
          contractAddress: orgData.contractAddress || orgId,
          tokenId: res.items[0].tokenId
        });
        if (data) {
          setDetail(data);
        }
      }
    }
    setPageload(false);
  };

  const handleGetVery = async () => {
    setPageload(true);
    const res = await apiGetGitcoinVery();
    if (res && res.state) {
      //  0=未捐赠，1=已捐赠，2=已发放SBT，3=已claim
      switch (res.state) {
        case 1:
          handleSendSbt();
          break;
        case 2:
          handleSearchSbt();
          setStep(1);
          break;
        case 3:
          setStep(4);
          break;
        default:
          break;
      }
    } else {
      setStep(0);
    }
    setPageload(false);
  };

  useEffect(() => {
    if (renderRef.current) {
      renderRef.current = false;
      handleGetVery();
      document.body.scrollTop = document.documentElement.scrollTop = 0;
      scrollTo(0, 0);
    }
  }, []);

  const renderDetail = () => {
    return (
      <ModalBody>
        {pageload ? (
          <div className="flex h-[50vh] w-[100%] items-center justify-center">
            <img src={IconLogoLoading} alt="" />
          </div>
        ) : (
          <div className="flex w-[100%] justify-between px-[44px] py-[40px]">
            <div className="ml-[12px] max-w-[555px]">
              <Typography
                variant="title"
                className="mt-[20px] max-w-[555px] line-clamp-1"
              >
                {item?.name}
                {/* Gitcoin Donator */}
              </Typography>
              <Typography className="mt-[14px] h-[80px] max-w-[555px] text-[#787878] line-clamp-4">
                {item?.desc}
              </Typography>
              <div className="mt-[40px] grid max-w-[555px] grid-flow-row-dense grid-cols-[repeat(auto-fit,_minmax(250px,_1fr))] gap-y-[16px] md:gap-x-[12px] lg:gap-x-[16px] xl:gap-x-[21px]">
                <div className="mb-[40px] w-[250px]">
                  <Typography className="mb-[8px]">Organization</Typography>
                  <div className="flex items-center">
                    <img
                      className="h-[26px] w-[26px]"
                      src={gitcoinLogo}
                      alt=""
                    />
                    <Typography className="ml-[10px] font-[600]">
                      {/* {item?.org.name} */}
                      Gitcoin
                    </Typography>
                  </div>
                </div>
                <div className="mb-[40px] w-[250px]">
                  <Typography className="mb-[8px]">Catalog</Typography>
                  <p className="flex items-center">
                    <img
                      className="mr-[5px] h-[22px] w-[22px]"
                      src={idIcon}
                      alt="id-sbt"
                    />
                    <Typography className="ml-[6px] font-[600]">
                      Donator Vitae
                    </Typography>
                  </p>
                </div>
                <div className="mb-[40px] w-[250px]">
                  <Typography className="mb-[8px]">Donation Times</Typography>
                  <Typography className="font-[600]">
                    {detail?.metadata?.attributes[0]['value']}
                  </Typography>
                </div>
                <div className="mb-[40px] w-[250px]">
                  <Typography className="mb-[8px]">Claim Timestamp</Typography>
                  <Typography className=" font-[600]">
                    {formatDate(detail?.createdAt || '2022-09-09T01:49:52Z')}{' '}
                    (UTC)
                  </Typography>
                </div>
              </div>
            </div>
            <svg
              className="ml-[38px] mr-[44px] hidden lg:flex"
              width={2}
              height={353}
              viewBox="0 0 2 353"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="none"
            >
              <path d="M1 0L1.00002 353" stroke="#F1F1F1" />
            </svg>
            <div className="relative">
              {loading ? (
                <div className="z-100 absolute top-0 flex h-[332px] w-[270px] items-center justify-center bg-white">
                  <img src={IconLogoLoading} alt="" />
                </div>
              ) : (
                ''
              )}
              <img
                className={`z-10 ${
                  item?.type === 'id'
                    ? 'h-[332px] w-[270px]'
                    : 'h-[270px] w-[270px]'
                }`}
                onLoad={() => setLoading(false)}
                src={preview}
                alt=""
              />
            </div>
          </div>
        )}
      </ModalBody>
    );
  };

  const renderSuccess = () => {
    return (
      <ModalBody className="h-[538px]">
        {showConfetti ? <Confetti width={995} height={620} /> : ''}
        <div className="mx-[86px] flex w-[100%] max-w-[822px] flex-col items-center justify-center">
          {/* <Typography variant="subTitle" className="mt-[20px]">
            Woohoo! You claimed a Vitae!
          </Typography> */}
          <img
            className="mt-[10px] h-[354px] w-auto max-w-[822px]"
            alt=""
            src={`${import.meta.env.VITE_BACKEND_CDN_URL}share/${orgId}/${
              item?.receiverAddress
            }/${detail?.tokenId}.png`}
          />
          <div className="mt-[43px] flex w-[100%] items-center justify-evenly">
            <Button
              className="border-[#ECF4EE] px-[30px]"
              style={{ boxShadow: '0px 2px 10px 0 #ecf4ee' }}
              variant="normal"
              onClick={() =>
                navigate(
                  `/user/${auth.address || localStorage.getItem('address')}`
                )
              }
              // onClick={() =>
              //   window.open(
              //     `https://opensea.io/assets/matic/${props.item?.contractAddress}/${props.item?.tokenId}`
              //   )
              // }
            >
              Check on Profile
            </Button>
            <Button
              onClick={handleShareTwitter}
              className="h-[36px] px-[30px]"
              variant="primary"
            >
              <svg
                className="mr-[5px]"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15.2644 5.94005C15.2735 6.07228 15.2735 6.20452 15.2735 6.33798C15.2735 10.4044 12.1779 15.0942 6.5173 15.0942V15.0918C4.84514 15.0942 3.20773 14.6152 1.80005 13.7121C2.04319 13.7414 2.28756 13.756 2.53253 13.7566C3.91827 13.7578 5.2644 13.2929 6.35459 12.4367C5.03771 12.4117 3.88293 11.5531 3.47951 10.2996C3.94082 10.3886 4.41614 10.3703 4.86891 10.2466C3.4332 9.9565 2.40029 8.69507 2.40029 7.23011C2.40029 7.21671 2.40029 7.20391 2.40029 7.19111C2.82808 7.42938 3.30706 7.56162 3.797 7.57624C2.44478 6.67253 2.02796 4.87362 2.84453 3.46716C4.407 5.38977 6.7123 6.55857 9.18701 6.68228C8.93899 5.61342 9.27781 4.49337 10.0773 3.742C11.3168 2.57685 13.2662 2.63657 14.4314 3.87545C15.1206 3.73956 15.7812 3.48667 16.3857 3.12835C16.1559 3.84072 15.6751 4.44584 15.0328 4.83036C15.6428 4.75845 16.2388 4.59514 16.8 4.3459C16.3869 4.96503 15.8665 5.50434 15.2644 5.94005Z"
                  fill="#4E4E4E"
                />
              </svg>
              Share via Twitter
            </Button>
          </div>
          <div
            className="mt-[30px] flex cursor-pointer items-center"
            onClick={() => window.open('https://discord.gg/Bg8Xz7YXWt')}
          >
            <img src={dcLogo} alt="" className="mr-[8px]" />
            <Typography className="underline decoration-solid">
              Join Vitae Discord to claim more
            </Typography>
          </div>
        </div>
      </ModalBody>
    );
  };

  const renderRejectConfirm = () => {
    return (
      <ModalBody>
        <div className="justify-content mt-[170px] flex w-[100%] flex-col items-center">
          <Typography variant="subTitle">
            You’ve claimed your Gitcoin NFT
          </Typography>
          <Button
            onClick={() =>
              navigate(
                `/user/${auth.address || localStorage.getItem('address')}`
              )
            }
            variant="normal"
            className="mt-[37px] border-[#B4F67175]"
          >
            Check on Profile
          </Button>
        </div>
      </ModalBody>
    );
  };

  const renderBody = () => {
    switch (step) {
      case 1:
        return renderDetail();
      case 2:
        return (
          <ModalBody>
            <div
              className="justify-cneter flex h-[100%] max-h-[568px] w-[100%] flex-col items-center"
              style={{
                backgroundImage: `url(${BgImg})`,
                backgroundPosition: '50% -145%',
                backgroundOrigin: 'border-box',
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'contain',
                boxSizing: 'border-box'
              }}
            >
              <Typography variant="subTitle" className="mt-[170px]">
                Minting the Vitae..
              </Typography>
            </div>
          </ModalBody>
        );
      case 3:
        return renderSuccess();
      case 4:
        return renderRejectConfirm();
      default:
        return (
          <ModalBody>
            <div className="mt-[60px] flex w-[100%] flex-col items-center justify-center">
              <Typography>
                You’re not eligible for claiming Gitcoin NFT,
              </Typography>
              <Typography>
                Please make sure you donate at least once on Gitcoin
              </Typography>
              <a
                href="https://gitcoin.co/"
                target="_blank"
                rel="noreferrer noopener"
              >
                <Typography className="underline decoration-solid">
                  https://gitcoin.co/
                </Typography>
              </a>
              <Button
                className="mt-[56px] border-[#B4F67175]"
                onClick={() => {
                  props.onClose ? props.onClose() : '';
                }}
                variant="normal"
              >
                OK
              </Button>
            </div>
          </ModalBody>
        );
    }
  };

  return (
    <Modal {...props}>
      <ModalHeader title="Get Gitcoin NFT" onClose={props.onClose} />
      {renderBody()}
      {!loading && step === 1 ? (
        <ModalFooter
          onConfirm={() => handleClaimSbt('accept')}
          onCancel={props.onClose}
          cancelText="Back"
          confirmText="Claim"
        />
      ) : (
        ''
      )}
    </Modal>
  );
};

export default ClaimModal;
